import type { MsrBeschreibung } from './typen';
import { herkunftMarke } from './gemeinsam';
import { zeitpunkt } from './kopf';
import {
  ContentSection, DataTable, EmptyState, StatusBadge, type Spalte,
} from '../../muster';

type Einstellwert = NonNullable<MsrBeschreibung['einstellwerte']>[number];

const EINSTELLWERT_SPALTEN: Spalte<Einstellwert>[] = [
  {
    schluessel: 'schluessel', titel: 'Schlüssel', immerSichtbar: true,
    wert: (e) => e.schluessel,
    inhalt: (e) => <span style={{ fontFamily: 'monospace' }}>{e.schluessel}</span>,
  },
  { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (e) => e.bezeichnung },
  { schluessel: 'wert', titel: 'Wert', wert: (e) => e.wert,
    inhalt: (e) => `${e.wert}${e.einheit ? ` ${e.einheit}` : ''}` },
  {
    schluessel: 'bereich', titel: 'Bereich', sortierbar: false,
    wert: (e) => (e.min === undefined && e.max === undefined
      ? null : `${e.min ?? '…'}…${e.max ?? '…'}`),
  },
  {
    schluessel: 'herkunft', titel: 'Herkunft', art: 'status', breite: '110px',
    wert: (e) => herkunftMarke(e.herkunft).text,
    inhalt: (e) => <StatusBadge {...herkunftMarke(e.herkunft)} klein />,
  },
  { schluessel: 'begruendung', titel: 'Begründung', wert: (e) => e.begruendung ?? null },
];

/**
 * Die Funktionsbeschreibung einer Anlage — KI-erzeugt mit Vorlagentext als
 * Rückfall (`Herkunft` 'ki' | 'vorlage'). Wird von Reiter 2 (Baum → Anlage)
 * UND Reiter 5 (GA-Funktionen) benutzt, deshalb eigene Datei.
 *
 * TM-8c: Ein Fehler beim Erzeugen steht beim Aufrufer (`AnlageDetail`, `Funktionen`), der den Abruf führt. Der
 * frühere eigene Fehlerzustand hier fing nur synchrone Würfe ab und kam im echten Ablauf nie zum Zug.
 */
export function Funktionsbeschreibung({
  beschreibung, onErzeugen, erzeugtLaeuft,
}: {
  beschreibung?: MsrBeschreibung | null;
  onErzeugen: (force: boolean) => void;
  erzeugtLaeuft: boolean;
}) {
  return (
    <ContentSection
      titel="Funktionsbeschreibung"
      meta={beschreibung && <StatusBadge {...herkunftMarke(beschreibung.herkunft)} klein />}
      aktionen={(
        <button type="button" className="m-knopf" disabled={erzeugtLaeuft}
          onClick={() => onErzeugen(!!beschreibung)}>
          {erzeugtLaeuft ? 'läuft …' : beschreibung ? 'erneuern' : 'erzeugen'}
        </button>
      )}
    >
      {!beschreibung && (
        <EmptyState
          titel="Noch keine Funktionsbeschreibung erzeugt."
          erklaerung={'Sie entsteht aus den abgeleiteten GA-Funktionen der Anlage — '
            + 'KI-erzeugt, mit dem Vorlagentext als Rückfall.'}
          aktion={(
            <button type="button" className="m-knopf m-knopf-haupt"
              disabled={erzeugtLaeuft} onClick={() => onErzeugen(false)}>
              {erzeugtLaeuft ? 'läuft …' : 'Beschreibung erzeugen'}
            </button>
          )}
        />
      )}

      {beschreibung && (
        <>
          <p style={{ whiteSpace: 'pre-wrap' }}>{beschreibung.beschreibung}</p>

          {beschreibung.regelstrategie && (
            <>
              <div className="feldname">Regelstrategie</div>
              <p style={{ whiteSpace: 'pre-wrap' }}>{beschreibung.regelstrategie}</p>
            </>
          )}

          {beschreibung.sicherheitsfunktionen.length > 0 && (
            <>
              <div className="feldname">Sicherheitsfunktionen</div>
              <ul>
                {beschreibung.sicherheitsfunktionen.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </>
          )}

          {beschreibung.betriebsarten.length > 0 && (
            <>
              <div className="feldname">Betriebsarten</div>
              <ul>
                {beschreibung.betriebsarten.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </>
          )}

          {beschreibung.hinweise.length > 0 && (
            <>
              <div className="feldname">Hinweise</div>
              <ul>
                {beschreibung.hinweise.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </>
          )}

          {beschreibung.einstellwerte && beschreibung.einstellwerte.length > 0 && (
            <>
              <div className="feldname">Einstellwerte</div>
              <DataTable
                name="Einstellwerte"
                spalten={EINSTELLWERT_SPALTEN}
                zeilen={beschreibung.einstellwerte}
                schluessel={(e) => e.id}
                dicht
                suche={false}
                spaltenwahl={false}
                leer={<EmptyState klein titel="Kein Einstellwert vorgeschlagen." />}
              />
            </>
          )}

          <div className="schwach" style={{ fontSize: 11, marginTop: 10 }}>
            {herkunftMarke(beschreibung.herkunft).text}
            {beschreibung.anbieter && ` · ${beschreibung.anbieter}`}
            {beschreibung.modell && ` · ${beschreibung.modell}`}
            {(beschreibung.eingabeTokens || beschreibung.ausgabeTokens) &&
              ` · ${beschreibung.eingabeTokens ?? 0}/${beschreibung.ausgabeTokens ?? 0} Token`}
            {' · '}
            {zeitpunkt(beschreibung.erzeugtAm)}
          </div>
        </>
      )}
    </ContentSection>
  );
}
